// @ts-nocheck
import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Loader2,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Calendar,
  MapPin,
  Users,
  DollarSign,
  ClipboardList,
} from "lucide-react";
import { format } from "date-fns";
import InlineRegistrationEditor from "./InlineRegistrationEditor";

const fmtCurrency = (n: number | null | undefined) =>
  n == null
    ? "$0"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n));

const fmtDate = (s: string | null | undefined) =>
  // Parse date-only strings ("2026-08-19") as LOCAL midnight so the calendar day
  // matches the Events list (plain new Date() treats them as UTC, shifting a day).
  s ? format(/^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + "T00:00:00") : new Date(s), "MMM d, yyyy") : "—";

const fmtDateTime = (s: string | null | undefined) =>
  s ? format(new Date(s), "MMM d, yyyy h:mm a") : "—";

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  confirmed: "bg-green-100 text-green-800 border-green-200",
  registered: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  withdrawn: "bg-red-100 text-red-800 border-red-200",
  waitlisted: "bg-amber-100 text-amber-800 border-amber-200",
};

const PAYMENT_COLORS: Record<string, string> = {
  paid: "bg-green-100 text-green-800 border-green-200",
  partial: "bg-amber-100 text-amber-800 border-amber-200",
  unpaid: "bg-red-100 text-red-800 border-red-200",
  refunded: "bg-slate-200 text-slate-700",
};

function Kpi({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: any;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide font-medium">
          <Icon className="h-4 w-4" /> {label}
        </div>
        <div className="mt-2 text-2xl font-bold">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

type SortKey =
  | "team_name"
  | "registration_status"
  | "payment_status"
  | "total_amount_due"
  | "total_amount_paid"
  | "balance_due"
  | "roster_size"
  | "registered_at";

export default function RegistrationDashboardPage() {
  const { scopeOrgId, isSuperAdmin, ready } = useAdminOrgScope();
  const queryClient = useQueryClient();

  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [paymentFilter, setPaymentFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("registered_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [openReg, setOpenReg] = useState<string | null>(null);

  // ---- Events (org-scoped) ----
  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ["regdash-events", scopeOrgId],
    enabled: ready,
    queryFn: async () => {
      let q = (supabase as any)
        .from("v_event_admin_dashboard")
        .select("*")
        .order("start_date", { ascending: false });
      if (scopeOrgId) q = q.eq("organization_id", scopeOrgId);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  // Default to first event once loaded
  const effectiveEventId = useMemo(() => {
    if (selectedEventId) return selectedEventId;
    return events[0]?.id ?? "";
  }, [selectedEventId, events]);

  const selectedEvent = useMemo(
    () => events.find((e: any) => e.id === effectiveEventId),
    [events, effectiveEventId]
  );

  // ---- Registrations for selected event ----
  const { data: rows = [], isLoading: rowsLoading } = useQuery({
    queryKey: ["regdash-regs", effectiveEventId],
    enabled: ready && !!effectiveEventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("v_team_registrations_summary")
        .select("*")
        .eq("event_id", effectiveEventId);
      if (error) throw error;
      return data ?? [];
    },
  });

  const distinctStatuses = useMemo(
    () => Array.from(new Set(rows.map((r: any) => r.registration_status).filter(Boolean))),
    [rows]
  );
  const distinctPayments = useMemo(
    () => Array.from(new Set(rows.map((r: any) => r.payment_status).filter(Boolean))),
    [rows]
  );

  const filtered = useMemo(() => {
    let list = [...rows];
    if (statusFilter !== "all")
      list = list.filter((r: any) => r.registration_status === statusFilter);
    if (paymentFilter !== "all")
      list = list.filter((r: any) => r.payment_status === paymentFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r: any) => (r.team_name ?? "").toLowerCase().includes(q));
    }
    list.sort((a: any, b: any) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (sortKey === "registered_at") {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      } else if (
        ["total_amount_due", "total_amount_paid", "balance_due", "roster_size"].includes(sortKey)
      ) {
        av = Number(av ?? 0);
        bv = Number(bv ?? 0);
      } else {
        av = (av ?? "").toString().toLowerCase();
        bv = (bv ?? "").toString().toLowerCase();
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [rows, statusFilter, paymentFilter, search, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "registered_at" ? "desc" : "asc");
    }
  };

  const SortHead = ({ k, label, className }: { k: SortKey; label: string; className?: string }) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className="inline-flex items-center gap-1 hover:text-foreground"
      >
        {label}
        {sortKey === k ? (
          sortDir === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    </TableHead>
  );

  const exportCsv = () => {
    const headers = [
      "Team",
      "Registration Status",
      "Payment",
      "Amount Due",
      "Paid",
      "Balance",
      "Roster Size",
      "Registered",
    ];
    const escape = (v: any) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(",")];
    filtered.forEach((r: any) => {
      lines.push(
        [
          r.team_name,
          r.registration_status,
          r.payment_status,
          r.total_amount_due,
          r.total_amount_paid,
          r.balance_due,
          r.roster_size,
          r.registered_at ? fmtDateTime(r.registered_at) : "",
        ]
          .map(escape)
          .join(",")
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `registrations-${selectedEvent?.slug || effectiveEventId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const invalidateList = () => {
    queryClient.invalidateQueries({ queryKey: ["regdash-regs", effectiveEventId] });
    queryClient.invalidateQueries({ queryKey: ["regdash-events", scopeOrgId] });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Registration Dashboard</h1>
        <p className="text-muted-foreground">
          Track team registrations, payments and check-in for your events.
        </p>
        {isSuperAdmin && (
          <p className="text-sm text-muted-foreground mt-1">
            Viewing all organizations' events (platform admin).
          </p>
        )}
      </div>

      {/* Event selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Event</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select
            value={effectiveEventId}
            onValueChange={(v) => setSelectedEventId(v)}
            disabled={eventsLoading || !events.length}
          >
            <SelectTrigger className="md:w-[420px]">
              <SelectValue placeholder={eventsLoading ? "Loading events..." : "Choose an event"} />
            </SelectTrigger>
            <SelectContent>
              {events
                .filter((e: any) => e.id)
                .map((e: any) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}
                    {e.start_date ? ` — ${fmtDate(e.start_date)}` : ""}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          {selectedEvent && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{selectedEvent.name}</span>
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {fmtDate(selectedEvent.start_date)}
                {selectedEvent.end_date ? ` – ${fmtDate(selectedEvent.end_date)}` : ""}
              </span>
              {(selectedEvent.venue_name || selectedEvent.venue_city) && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {[selectedEvent.venue_name, selectedEvent.venue_city, selectedEvent.venue_state]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              )}
              <Badge className={STATUS_COLORS[selectedEvent.status] ?? ""} variant="outline">
                {selectedEvent.status ?? "draft"}
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {!ready ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading...
        </div>
      ) : !effectiveEventId ? (
        <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
          No events found for this organization.
        </div>
      ) : (
        <>
          {/* KPI cards */}
          {selectedEvent && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi
                icon={ClipboardList}
                label="Total Registrations"
                value={selectedEvent.total_registrations ?? 0}
              />
              <Kpi
                icon={ClipboardList}
                label="Paid / Draft"
                value={
                  <>
                    {selectedEvent.paid_registrations ?? 0}
                    <span className="text-muted-foreground text-lg"> / {selectedEvent.draft_registrations ?? 0}</span>
                  </>
                }
                sub="paid / draft registrations"
              />
              <Kpi
                icon={DollarSign}
                label="Collected / Outstanding"
                value={fmtCurrency(selectedEvent.total_collected)}
                sub={`${fmtCurrency(selectedEvent.total_outstanding)} outstanding`}
              />
              <Kpi
                icon={Users}
                label="Teams / Roster"
                value={selectedEvent.unique_teams_registered ?? 0}
                sub={`${selectedEvent.total_roster_size ?? 0} total roster size`}
              />
            </div>
          )}

          {/* Filters */}
          <Card>
            <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between pb-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1">
                <Input
                  placeholder="Search team name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {distinctStatuses.map((s: string) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All payments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All payments</SelectItem>
                    {distinctPayments.map((s: string) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={exportCsv} disabled={!filtered.length}>
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              {rowsLoading ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading registrations...
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  No registrations match your filters.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortHead k="team_name" label="Team" />
                      <SortHead k="registration_status" label="Status" />
                      <SortHead k="payment_status" label="Payment" />
                      <SortHead k="total_amount_due" label="Due" className="text-right" />
                      <SortHead k="total_amount_paid" label="Paid" className="text-right" />
                      <SortHead k="balance_due" label="Balance" className="text-right" />
                      <SortHead k="roster_size" label="Roster" className="text-right" />
                      <SortHead k="registered_at" label="Registered" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((r: any) => {
                      const expanded = openReg === r.event_registration_id;
                      return (
                        <React.Fragment key={r.event_registration_id}>
                          <TableRow
                            className="cursor-pointer"
                            data-state={expanded ? "selected" : undefined}
                            onClick={() =>
                              setOpenReg(expanded ? null : r.event_registration_id)
                            }
                          >
                            <TableCell className="font-medium">{r.team_name ?? "—"}</TableCell>
                            <TableCell>
                              <Badge
                                className={STATUS_COLORS[r.registration_status] ?? ""}
                                variant="outline"
                              >
                                {r.registration_status ?? "—"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={PAYMENT_COLORS[r.payment_status] ?? ""}
                                variant="outline"
                              >
                                {r.payment_status ?? "—"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              {fmtCurrency(r.total_amount_due)}
                            </TableCell>
                            <TableCell className="text-right">
                              {fmtCurrency(r.total_amount_paid)}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {fmtCurrency(r.balance_due)}
                            </TableCell>
                            <TableCell className="text-right">{r.roster_size ?? 0}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {fmtDate(r.registered_at)}
                            </TableCell>
                          </TableRow>
                          {expanded && (
                            <TableRow className="hover:bg-transparent">
                              <TableCell colSpan={8} className="bg-muted/30 p-4">
                                <InlineRegistrationEditor
                                  registrationId={r.event_registration_id}
                                  onClose={() => setOpenReg(null)}
                                  onSaved={() => {
                                    invalidateList();
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
